import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { AlertTriangle, Loader2, DollarSign, History } from 'lucide-react';
import { toast } from 'sonner';

export const usd = (n) => { const v = Number(n || 0); return `USD ${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
export const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const PLAN_NAMES = { free: 'Free Trial', basic: 'Starter', pro: 'Professional', enterprise: 'Enterprise' };

function Row({ label, value, testid, strong }) {
    return (
        <div className={`flex justify-between text-sm ${strong ? 'font-bold border-t border-border/50 pt-2 mt-1' : ''}`}>
            <span className={strong ? '' : 'text-muted-foreground'}>{label}</span>
            <span className="font-mono" data-testid={testid}>{value}</span>
        </div>
    );
}

function Breakdown({ b }) {
    return (
        <div className="space-y-1.5">
            <Row label={`${b.aircraft_count} aeronaves facturables × ${usd(b.price_per_aircraft)} (${b.plan_name})`} value={usd(b.aircraft_subtotal)} testid="billing-aircraft-subtotal" />
            <p className="text-[11px] text-muted-foreground" data-testid="billing-billable-note">Incluye todas las aeronaves salvo las almacenadas.</p>
            {b.modules.map(m => <Row key={m.key} label={`Módulo ${m.label}`} value={usd(m.price_monthly_usd)} testid={`billing-module-${m.key}`} />)}
            {b.legacy_all_modules && <p className="text-[11px] text-muted-foreground">Módulos en modo heredado (todos habilitados, sin cargo hasta que se definan).</p>}
            <Row label="Total de lista" value={usd(b.list_total)} testid="billing-list-total" />
            {b.founder_discount_pct > 0 && <Row label={`Precio fundador −${b.founder_discount_pct}%${b.founder_discount_until ? ` hasta ${fmtDate(b.founder_discount_until)}` : ''}`} value={`− ${usd(b.founder_discount_amount)}`} testid="billing-founder-discount" />}
            <Row label="Total mensual" value={usd(b.total_monthly)} testid="billing-total-monthly" strong />
            <Row label="A cobrar este mes" value={usd(b.amount_due_monthly)} testid="billing-amount-due" strong />
        </div>
    );
}

function PricingForm({ tenant, apiCall, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setForm({
            descuento_fundador_porcentaje: tenant.descuento_fundador_porcentaje ?? '',
            descuento_fundador_hasta: tenant.descuento_fundador_hasta || '',
            piloto_hasta: tenant.piloto_hasta || '',
        });
    }, [tenant]);
    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.descuento_fundador_porcentaje === '' && tenant.descuento_fundador_porcentaje == null) delete payload.descuento_fundador_porcentaje;
            else payload.descuento_fundador_porcentaje = Number(payload.descuento_fundador_porcentaje || 0);
            await apiCall('patch', `/admin/tenants/${tenant.id}/pricing`, payload);
            toast.success('Condiciones comerciales guardadas');
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };
    const field = (k, label, type) => (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            <Input type={type} value={form[k] ?? ''} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} className="h-8 text-xs font-mono" data-testid={`pricing-${k}`} />
        </div>
    );
    return (
        <div className="pt-3 mt-3 border-t border-border/50 space-y-3">
            <p className="text-[11px] text-muted-foreground">Solo visible para superadmin. La aerolínea nunca ve estos campos.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field('descuento_fundador_porcentaje', 'Descuento fundador %', 'number')}
                {field('descuento_fundador_hasta', 'Descuento hasta', 'date')}
                {field('piloto_hasta', 'Piloto hasta', 'date')}
            </div>
            <div className="flex justify-end">
                <Button size="sm" onClick={save} disabled={saving} data-testid="save-pricing-btn">
                    {saving && <Loader2 className="w-3 h-3 mr-2 animate-spin" />} Guardar condiciones
                </Button>
            </div>
        </div>
    );
}

function PricingHistory({ items }) {
    if (!items?.length) return null;
    return (
        <div className="pt-3 mt-3 border-t border-border/50" data-testid="pricing-history">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Historial comercial</p>
            {items.slice(0, 8).map(h => (
                <div key={h.id} className="text-[11px] text-muted-foreground py-0.5">
                    <span className="font-mono">{h.created_at?.slice(0, 16).replace('T', ' ')}</span> · {h.user_email} ·{' '}
                    {Object.entries(h.changes || {}).map(([k, v]) => `${k}: ${v.before ?? '—'} → ${v.after ?? '—'}`).join(', ')}
                </div>
            ))}
        </div>
    );
}

export function TenantBillingCard({ detail, apiCall, onSaved, editable }) {
    const b = detail.billing;
    if (!b) return null;
    return (
        <Card className="bg-muted/30" data-testid="tenant-billing-card">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground flex items-center gap-2"><DollarSign className="w-4 h-4" /> Cobro mensual</p>
                    {b.pilot_active && <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20" data-testid="pilot-badge">Piloto hasta {fmtDate(b.pilot_until)}</Badge>}
                </div>
                {b.out_of_range && (
                    <div className="mb-3 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-800 dark:text-amber-300 flex gap-2" data-testid="plan-range-warning">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>{b.aircraft_count} aeronaves facturables quedan fuera del rango de {b.plan_name}. Plan sugerido: <strong>{PLAN_NAMES[b.suggested_plan]}</strong>. El cambio se aplica manualmente.</span>
                    </div>
                )}
                <Breakdown b={b} />
                {detail.tenant?.planned_aircraft != null && (
                    <p className="text-[11px] text-muted-foreground mt-2" data-testid="billing-planned-aircraft">Aeronaves previstas al alta: {detail.tenant.planned_aircraft}</p>
                )}
                {editable && <PricingForm tenant={detail.tenant} apiCall={apiCall} onSaved={onSaved} />}
                <PricingHistory items={detail.pricing_history} />
            </CardContent>
        </Card>
    );
}
